import React, {useState, useRef, useEffect} from 'react';

export interface DropdownOption {
  name: string;
  code: number;
}

export type OnDropdownChangeInput = {code: number; name: string};

interface DashDropdownProps {
  options: DropdownOption[];
  selectedValue?: number;
  onChange: ({code, name}: OnDropdownChangeInput) => void;
  placeholder?: string;
}

export const DashDropdown: React.FC<DashDropdownProps> = ({
  options,
  selectedValue,
  onChange,
  placeholder = 'Select a location...',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isHovered, setIsHovered] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedOption = options.find(opt => opt.code === selectedValue);

  const filteredOptions = options.filter(opt =>
    opt.name.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const baseInputStyle: React.CSSProperties = {
    width: '100%',
    height: '46px',
    padding: '0 16px',
    borderRadius: '8px',
    border: `1px solid ${isOpen || isHovered ? '#daff00' : '#333'}`,
    backgroundColor: '#2a2a2a',
    color: selectedOption ? '#fff' : '#777',
    fontSize: '0.85rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
    boxSizing: 'border-box',
    transition: 'all 0.2s ease',
  };

  const listContainerStyle: React.CSSProperties = {
    position: 'absolute',
    top: '52px',
    left: 0,
    right: 0,
    backgroundColor: '#2a2a2a',
    borderRadius: '8px',
    border: '1px solid #333',
    boxShadow: '0 10px 25px rgba(0,0,0,0.5)',
    zIndex: 1000,
    maxHeight: '250px',
    overflowY: 'auto',
    overflowX: 'hidden',
    boxSizing: 'border-box',
  };

  const searchInputStyle: React.CSSProperties = {
    width: '100%',
    padding: '12px',
    backgroundColor: '#1a1a1a',
    border: 'none',
    borderBottom: '1px solid #333',
    color: '#fff',
    outline: 'none',
    fontSize: '0.9rem',
    position: 'sticky',
    top: 0,
    boxSizing: 'border-box',
  };

  return (
    <div
      ref={containerRef}
      style={{position: 'relative', width: '100%'}}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}>
      {/* Trigger: The "Button" part of the dropdown */}
      <div style={baseInputStyle} onClick={() => setIsOpen(!isOpen)}>
        <span>{selectedOption ? selectedOption.name : placeholder}</span>
        <span
          style={{
            color: isOpen || isHovered ? '#daff00' : '#666',
            fontSize: '0.7rem',
          }}>
          {isOpen ? '▲' : '▼'}
        </span>
      </div>

      {/* Dropdown Menu */}
      {isOpen && (
        <div style={listContainerStyle}>
          <input
            autoFocus
            type="text"
            placeholder="Search location..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            style={searchInputStyle}
            onClick={e => e.stopPropagation()}
          />

          {filteredOptions.length > 0 ? (
            filteredOptions.map(opt => (
              <div
                key={opt.code}
                onClick={() => {
                  onChange({code: opt.code, name: opt.name});
                  setIsOpen(false);
                  setSearchTerm('');
                }}
                style={{
                  padding: '12px 16px',
                  cursor: 'pointer',
                  color: selectedValue === opt.code ? '#daff00' : '#fff',
                  backgroundColor: 'transparent',
                  transition: 'background 0.2s',
                }}
                onMouseEnter={e =>
                  (e.currentTarget.style.backgroundColor = '#333')
                }
                onMouseLeave={e =>
                  (e.currentTarget.style.backgroundColor = 'transparent')
                }>
                {opt.name}
              </div>
            ))
          ) : (
            <div style={{padding: '12px', color: '#777', textAlign: 'center'}}>
              No locations found
            </div>
          )}
        </div>
      )}
    </div>
  );
};
